import axios from "axios";
import React, { useEffect, useState } from "react";
import { getItem } from "./localStorageUtils";

const MultiFileUpload = () => {
    const baseURL = process.env.REACT_APP_Baseurl
    const token = getItem('access_token')
    const [files, setFiles] = useState([]);
    const [uploadingFiles, setUploadingFiles] = useState([]);
    const [cancellationControllers, setCancellationControllers] = useState([]);

    // Helper function to format file size
    const formatBytes = (bytes) => {
        if (bytes === 0) return "0 B";
        const units = ["B", "KB", "MB", "GB", "TB"];
        const i = Math.floor(Math.log(bytes) / Math.log(1024));
        return (bytes / Math.pow(1024, i)).toFixed(2) + " " + units[i];
    };


    const getVideoDuration = (file) => {
        return new Promise((resolve) => {
            const videoElement = document.createElement("video");
            videoElement.onloadedmetadata = () => {
                resolve(videoElement.duration);
            };
            videoElement.src = URL.createObjectURL(file);
        });
    };

    const handleFileChange = async (event) => {
        const selectedFiles = Array.from(event.target.files);
        const filesWithDuration = await Promise.all(
            selectedFiles.map(async (file) => {
                const duration = await getVideoDuration(file);
                return { file, duration };
            })
        );
        setFiles((prevFiles) => [...prevFiles, ...filesWithDuration]);
    };

    const CircularProgress = ({ progress }) => {
        const radius = 50;
        const strokeWidth = 8;
        const circumference = 2 * Math.PI * radius;
        const offset = circumference - (progress / 100) * circumference;
        return (
            <svg width={120} height={120} viewBox="0 0 120 120">
                <circle cx="60" cy="60" r={radius} stroke="#e6e6e6" strokeWidth={strokeWidth} fill="none" />
                <circle cx="60" cy="60" r={radius} stroke="#4db8ff" strokeWidth={strokeWidth} fill="none" strokeDasharray={circumference} strokeDashoffset={offset} />
                <text x="50%" y="50%" alignmentBaseline="middle" textAnchor="middle" fontSize="18px" fill="#333">
                    {progress}%
                </text>
            </svg>
        );
    };

    // Start the upload process
    const handleUpload = () => {
        const newFilesToUpload = files.filter((file) => {
            return !uploadingFiles.some(
                (uploadedFile) => uploadedFile.file === file.file && uploadedFile.status !== "completed"
            );
        });

        if (newFilesToUpload.length === 0) {
            alert("No new files to upload.");
            return;
        }

        const newUploadingFiles = newFilesToUpload.map((fileItem) => ({
            file: fileItem.file,
            progress: 0,
            status: "pending",
            duration: fileItem.duration,
            // startTime: Date.now(), // Track start time for speed calculation
            uploaded: 0, // Track uploaded bytes
        }));

        setUploadingFiles((prev) => [...prev, ...newUploadingFiles]);

        newFilesToUpload.forEach((fileItem, index) => {
            uploadFile(fileItem.file, index);
        });
    };

    const uploadFile = async (file, index) => {
        const formData = new FormData();
        formData.append("file", file);

        const cancelController = new AbortController();
        setCancellationControllers((prev) => [...prev, cancelController]);

        // Ensure the file is added to the uploadingFiles with the necessary properties
        setUploadingFiles((prev) => {
            const updatedFiles = [...prev];
            updatedFiles[index] = {
                ...updatedFiles[index],
                status: "uploading",  // Update status to 'uploading'
                uploaded: 0,  // Initial uploaded bytes
                speed: 0,  // Initial speed
            };
            return updatedFiles;
        });
        const startTime = Date.now();
        try {
            const response = await axios.post(`${baseURL}/admin/video-upload-fdile`,
                formData,
                {
                    headers: {
                        "Authorization": `Bearer ${token}`,
                        "Content-Type": "multipart/form-data",
                    },
                    onUploadProgress: (progressEvent) => {
                        if (progressEvent.total && progressEvent.loaded) {
                            const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);

                            // Calculate per-second speed (bytes per second)
                            const currentTime = Date.now();
                            const elapsedTime = (currentTime - startTime) / 1000; // in seconds
                            const speed = elapsedTime > 0 ? Math.round(progressEvent.loaded / elapsedTime) : 0;

                            // Update progress and speed
                            setUploadingFiles((prev) => {
                                const updatedFiles = [...prev];
                                updatedFiles[index].progress = percent;
                                updatedFiles[index].uploaded = progressEvent.loaded;
                                updatedFiles[index].speed = speed;
                                return updatedFiles;
                            });
                        }
                    },
                    signal: cancelController.signal, // Attach abort controller to the request
                }
            );

            if (response.status === 200) {
                setUploadingFiles((prev) => {
                    const updatedFiles = [...prev];
                    updatedFiles[index].status = "completed"; // Mark as completed on success
                    return updatedFiles;
                });
            } else {
                throw new Error("Upload failed");
            }
        } catch (error) {
            if (axios.isCancel(error)) {
                console.log("Upload canceled:", error.message);
            } else {
                console.error("Error during upload", error);
            }
            setUploadingFiles((prev) => {
                const updatedFiles = [...prev];
                updatedFiles[index].status = "failed";
                updatedFiles[index].message = error?.response?.data?.message;// Mark as failed on error
                return updatedFiles;
            });
        }
    };


    // Cancel upload
    const cancelUpload = (index) => {
        const cancelController = cancellationControllers[index];
        if (cancelController) {
            cancelController.abort();
            setUploadingFiles((prev) => {
                const updatedFiles = [...prev];
                updatedFiles[index].status = "canceled";
                return updatedFiles;
            });
        }
    };

    // Retry upload for a failed file
    const retryUpload = (index) => {
        const fileToRetry = uploadingFiles[index].file;
        setUploadingFiles((prev) => {
            const updatedFiles = [...prev];
            updatedFiles[index].status = "pending"; // Reset status to 'pending'
            updatedFiles[index].progress = 0; // Reset progress
            return updatedFiles;
        });
        uploadFile(fileToRetry, index); // Retry upload
    };

    useEffect(() => {
        const handleBeforeUnload = (event) => {
            // Check if there are any uploads in progress
            const inProgressUploads = uploadingFiles.some((file) => file.status === "uploading");
            if (inProgressUploads) {
                const message = "You have an ongoing upload. Are you sure you want to leave?";
                event.returnValue = message;
                return message;
            }
        };

        window.addEventListener("beforeunload", handleBeforeUnload);

        return () => {
            window.removeEventListener("beforeunload", handleBeforeUnload);
        };
    }, [uploadingFiles]);

    return (
        <div className="position-relative">
            <input type="file" accept="video/*" multiple onChange={handleFileChange} />
            <button onClick={handleUpload}>Upload Files</button>
            {uploadingFiles.map((fileItem, index) => (
                <div key={index} className="position-relative">
                    <span>{fileItem?.from}</span>
                    <h4>{fileItem.file.name}</h4>
                    <div>Status: {fileItem.status}</div>
                    <div>Duration: {fileItem.duration ? `${Math.round(fileItem.duration)} seconds` : "Unknown"}</div>
                    <div>Size: {formatBytes(fileItem.file.size)}</div>
                    {fileItem.status === "uploading" && (
                        <>
                            <progress value={fileItem.progress} max="100">{fileItem.progress}%</progress>
                            <CircularProgress progress={fileItem.progress} />
                            <div>Speed: {fileItem.speed ? formatBytes(fileItem.speed) + "/s" : "Calculating..."}</div>
                        </>
                    )}
                    {fileItem.status === "completed" && <span>Upload Complete</span>}
                    {fileItem.status === "failed" && (
                        <div>
                            <span>{`Upload Failed ${fileItem?.message}`}</span>
                            <button onClick={() => retryUpload(index)}>Retry</button>
                        </div>
                    )}
                    {fileItem.status === "canceled" && <span>Upload Canceled</span>}
                    {(fileItem.status === "uploading" || fileItem.status === "pending") && (
                        <button onClick={() => cancelUpload(index)}>Cancel</button>
                    )}
                </div>
            ))}
        </div>
    );
};

export default MultiFileUpload;
